/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */


import { FileDropZone } from '../files/FileDropZone'
import {
  InputGroup,
  InputGroupText,
  Input,
  Button
} from 'reactstrap'
import { FileListContainer, FileListContainerProps } from '../files/FileListContainer'
import React from 'react'
import SimpleTooltip from '../simple-tooltip';
import { nl2br } from '../../utils';
import { SupportingEvidenceTooltip } from './SupportingEvidenceTooltip';
import { CollapseButton, CollapseContent, CollapsePanel } from '../collapse-panel';
import { BasicAlert } from '../alert/BasicAlert';
import { EvidenceLink } from '../../types/universalTracker';
import { HandleFileDescriptionFn } from '../../types/file';
import { QUESTION } from '../../constants/terminology';


function displayEvidenceInstructions(evidenceInstructions: string) {
  if (!evidenceInstructions) {
    return '';
  }

  const tooltip = <div className='text-left'>{nl2br(evidenceInstructions)}</div>;

  return <SimpleTooltip placement={'top'} text={tooltip}>
    <Button color='link' size='sm'>
      <u>Suggested Evidence</u>
    </Button>
  </SimpleTooltip>
}

interface EvidenceProps extends FileListContainerProps {
  disabled: boolean;
  handleEvidenceLinkAdd: (link: any, isPublic: boolean) => void;
  handleFileDescriptionAdd: HandleFileDescriptionFn;
  handleFilesAdded: (addFiles: File[]) => void;
  isEvidenceRequired?: boolean;
  evidenceInstructions: string;
  isContributor: boolean;
  isVerifier: boolean;
}

const defaultEvidenceLink: EvidenceLink = { url: '', description: '' };

export const EvidenceInput = (props: EvidenceProps) => {

  const [publicEvidenceLink, setPublicLink] = React.useState<EvidenceLink>(defaultEvidenceLink);
  const [privateEvidenceLink, setPrivateLink] = React.useState<EvidenceLink>(defaultEvidenceLink);

  const {
    disabled,
    handleEvidenceLinkAdd,
    handleFilesAdded,
    isEvidenceRequired,
    evidenceInstructions,
    isContributor,
    isVerifier,
    existingFiles,
    files,
  } = props;

  const hasAction = isContributor || isVerifier;
  const hasExistingFiles = existingFiles && existingFiles.length > 0;

  const updateLink = (value: EvidenceLink, isPublic: boolean) => {
    handleEvidenceLinkAdd(value, isPublic);
    isPublic ? setPublicLink(defaultEvidenceLink) : setPrivateLink(defaultEvidenceLink);
  }

  const hasData = files.length > 0 || (existingFiles && existingFiles.length > 0);

  return (
    <CollapsePanel className='py-3 border-top border-bottom' collapsed={!isEvidenceRequired && !hasExistingFiles} >
      <CollapseButton className='d-block'>
        <h5 className='d-flex align-items-center gap-3 text-ThemeAccentExtradark'>
          Supporting Evidence {isEvidenceRequired ? '*' : ''}
          <div className='evidence-tooltip-icon'>
            <SupportingEvidenceTooltip />
          </div>
        </h5>
      </CollapseButton>
      <CollapseContent>
        <div className='form-confirmation-input pt-3'>
          <h2 className={'title-container'}>
            <span></span>
            <span className='text-right'>
              {displayEvidenceInstructions(evidenceInstructions)}
            </span>
          </h2>
          {hasAction && !disabled ?
            <div>
              <FileDropZone disabled={disabled} multiple={true} onDrop={handleFilesAdded} />

              <div className='d-flex mt-3 gap-1 align-items-center'>
                <InputGroup>
                  <InputGroupText>
                    <i className='fa fa-link' />
                  </InputGroupText>
                  <Input
                    key='input-evidence-link'
                    type='text'
                    disabled={disabled}
                    placeholder='Add a link to EXTERNAL files'
                    value={publicEvidenceLink.url}
                    onChange={(e) => setPublicLink({ ...publicEvidenceLink, url: e.target.value })}
                  />
                </InputGroup>
                <InputGroup className='w-25'>
                  <Input
                    key='evidence-link-description'
                    type='text'
                    disabled={disabled}
                    placeholder='Page reference'
                    value={publicEvidenceLink.description}
                    onChange={(e) => setPublicLink({ ...publicEvidenceLink, description: e.target.value })}
                  />
                </InputGroup>
                <Button
                  color='primary'
                  disabled={!publicEvidenceLink.url}
                  onClick={() => updateLink(publicEvidenceLink, true)}
                >
                  Add
                </Button>
              </div>

              <div className='d-flex mt-3 gap-1 align-items-center'>
                <InputGroup>
                  <InputGroupText>
                    <i className='fa fa-lock' />
                  </InputGroupText>
                  <Input
                    key='input-evidence-link'
                    type='text'
                    disabled={disabled}
                    placeholder='Add a reference or link to INTERNAL files'
                    value={privateEvidenceLink.url}
                    onChange={(e) => setPrivateLink({ ...privateEvidenceLink, url: e.target.value })}
                  />
                </InputGroup>
                <InputGroup className='w-25'>
                  <Input
                    key='evidence-link-description'
                    type='text'
                    disabled={disabled}
                    placeholder='Page reference'
                    value={privateEvidenceLink.description}
                    onChange={(e) => setPrivateLink({ ...privateEvidenceLink, description: e.target.value })}
                  />
                </InputGroup>
                <Button
                  color='primary'
                  disabled={!privateEvidenceLink.url}
                  onClick={() => updateLink(privateEvidenceLink, false)}
                >
                  Add
                </Button>
              </div>
            </div>
            : null}

          {(!hasAction && !hasData)
            ?
            <BasicAlert type='info'>There are no evidence files for this {QUESTION.SINGULAR}</BasicAlert>
            :
            <FileListContainer
              title={'Evidence to be added'}
              {...props}
              allowToggle={props.isContributor}
            />
          }

        </div>
      </CollapseContent>
    </CollapsePanel>
  )
}
